import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  DEFAULT_TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER,
  isTaskTrackerStickerFilterDraftEmpty,
  mapStickerFiltersAppliedToDraft,
  mapStickerFiltersDraftToApi,
  serializeTaskTrackerStickerFiltersApplied,
  serializeTaskTrackerStickerFiltersDraft,
  sanitizeTaskTrackerBoardStickerFilters,
  shouldSyncBoardStickerFiltersFromApi,
  TaskTrackerStickerFilterDraftValue,
} from '../../lib/taskTrackerBoard.stickerFilters.utils'
import { createBoardStickerFiltersState, writeStoredBoardStickerFilters } from '../../lib/taskTrackerBoardStickerFilters.storage'
import { writeStoredBoardCompletedStatusFilter } from '../../lib/taskTrackerBoardCompletedStatusFilters.storage'
import type {
  TaskTrackerBoardCompletedStatusFilter,
  TaskTrackerBoardStickerFilter,
  TaskTrackerStickerFiltersDraft,
} from '../domain.types'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  createEmptyBoardStickerFiltersBoardState,
  createInitialBoardStickerFiltersState,
} from './boardStickerFilters.state.utils'

const patchDraftFilter = (
  prev: TaskTrackerStickerFiltersDraft,
  fieldSettingId: number,
  value: TaskTrackerStickerFilterDraftValue,
): TaskTrackerStickerFiltersDraft => {
  const next = { ...prev }

  if (isTaskTrackerStickerFilterDraftEmpty(value)) {
    delete next[fieldSettingId]
  } else {
    next[fieldSettingId] = value
  }

  return next
}

const ensureBoard = (
  state: ReturnType<typeof createInitialBoardStickerFiltersState>,
  boardId: number,
) => {
  if (!state.byBoardId[boardId]) {
    state.byBoardId[boardId] = createEmptyBoardStickerFiltersBoardState()
  }
  return state.byBoardId[boardId]
}

const boardStickerFiltersSlice = createSlice({
  name: 'taskTrackerBoardStickerFilters',
  initialState: createInitialBoardStickerFiltersState(),
  reducers: {
    switchBoardStickerFilters(state, action: PayloadAction<number | null>) {
      if (action.payload == null) return
      const nextState = createBoardStickerFiltersState(action.payload)
      state.byBoardId[action.payload] = nextState
    },
    setBoardStickerDraftFilter(
      state,
      action: PayloadAction<{
        boardId: number
        fieldSettingId: number
        value: TaskTrackerStickerFilterDraftValue
      }>,
    ) {
      const filtersState = ensureBoard(state, action.payload.boardId)
      const next = patchDraftFilter(
        filtersState.draftFilters,
        action.payload.fieldSettingId,
        action.payload.value,
      )
      filtersState.draftFilters = next
      const nextApplied = mapStickerFiltersDraftToApi(next)
      if (
        serializeTaskTrackerStickerFiltersApplied(filtersState.appliedFilters) !==
        serializeTaskTrackerStickerFiltersApplied(nextApplied)
      ) {
        filtersState.appliedFilters = nextApplied
      }
      writeStoredBoardStickerFilters(action.payload.boardId, next)
    },
    resetBoardStickerFilters(state, action: PayloadAction<number>) {
      const filtersState = ensureBoard(state, action.payload)
      filtersState.draftFilters = {}
      filtersState.appliedFilters = []
      filtersState.completedStatus = DEFAULT_TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER
      writeStoredBoardStickerFilters(action.payload, {})
      writeStoredBoardCompletedStatusFilter(
        action.payload,
        DEFAULT_TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER,
      )
    },
    setBoardCompletedStatusFilter(
      state,
      action: PayloadAction<{
        boardId: number
        completedStatus: TaskTrackerBoardCompletedStatusFilter
      }>,
    ) {
      const filtersState = ensureBoard(state, action.payload.boardId)
      filtersState.completedStatus = action.payload.completedStatus
      writeStoredBoardCompletedStatusFilter(
        action.payload.boardId,
        action.payload.completedStatus,
      )
    },
    pruneBoardStickerFilters(
      state,
      action: PayloadAction<{ boardId: number; visibleFieldSettingIds: number[] }>,
    ) {
      const visibleFieldSettingIds = action.payload.visibleFieldSettingIds
      // Пустой список = стикеры ещё не загрузились, не чистим сохранённые фильтры
      if (visibleFieldSettingIds.length === 0) return

      const filtersState = ensureBoard(state, action.payload.boardId)
      const visibleIds = new Set(visibleFieldSettingIds)
      const hasHiddenFilters = Object.keys(filtersState.draftFilters).some(
        (fieldSettingId) => !visibleIds.has(Number(fieldSettingId)),
      )
      if (!hasHiddenFilters) return

      const next: TaskTrackerStickerFiltersDraft = {}
      Object.entries(filtersState.draftFilters).forEach(([fieldSettingId, value]) => {
        const id = Number(fieldSettingId)
        if (visibleIds.has(id)) {
          next[id] = value
        }
      })
      filtersState.draftFilters = next
      const nextApplied = mapStickerFiltersDraftToApi(next)
      if (
        serializeTaskTrackerStickerFiltersApplied(filtersState.appliedFilters) !==
        serializeTaskTrackerStickerFiltersApplied(nextApplied)
      ) {
        filtersState.appliedFilters = nextApplied
      }
      writeStoredBoardStickerFilters(action.payload.boardId, next)
    },
    syncBoardStickerAppliedFilters(
      state,
      action: PayloadAction<{
        boardId: number
        filters: TaskTrackerBoardStickerFilter[]
      }>,
    ) {
      const filtersState = ensureBoard(state, action.payload.boardId)
      const syncedFilters = sanitizeTaskTrackerBoardStickerFilters(
        action.payload.filters,
      )

      if (
        !shouldSyncBoardStickerFiltersFromApi(
          filtersState.appliedFilters,
          syncedFilters,
        )
      ) {
        return
      }

      filtersState.appliedFilters = syncedFilters
      const nextDraft = mapStickerFiltersAppliedToDraft(
        filtersState.appliedFilters,
      )
      if (
        serializeTaskTrackerStickerFiltersDraft(filtersState.draftFilters) !==
        serializeTaskTrackerStickerFiltersDraft(nextDraft)
      ) {
        filtersState.draftFilters = nextDraft
        writeStoredBoardStickerFilters(action.payload.boardId, nextDraft)
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(removeBoard, (state, action) => {
      delete state.byBoardId[action.payload.boardId]
    })
  },
})

export const {
  switchBoardStickerFilters,
  setBoardStickerDraftFilter,
  setBoardCompletedStatusFilter,
  resetBoardStickerFilters,
  pruneBoardStickerFilters,
  syncBoardStickerAppliedFilters,
} = boardStickerFiltersSlice.actions

export default boardStickerFiltersSlice.reducer
